import { API } from '../utils/amplifyWrapper';

/**
 * Generate audio asset (sound effect or music) using fal.ai
 * @param {Object} params - Generation parameters
 * @param {string} params.org_id - Organization ID
 * @param {string} params.integration_id - Integration ID
 * @param {string} params.type - Asset type ('sound-effect' or 'music')
 * @param {string} params.prompt - Text prompt describing the audio
 * @param {string} params.socketConnectionId - WebSocket connection ID for async generation
 * @param {number} [params.seconds_total] - Duration for sound effects (1-30, default: 30)
 * @param {number} [params.duration] - Duration for music (5-240, default: 60)
 * @param {boolean} [params.instrumental] - Whether to generate instrumental music
 * @returns {Promise<Object>} Generated audio asset data
 */
export const generateAudioAssetService = async ({ 
    org_id, 
    integration_id, 
    type,
    prompt, 
    socketConnectionId,
    seconds_total,
    duration,
    instrumental
}) => {
    try {
        const body = {
            type,
            prompt,
            socketConnectionId
        };
        
        // Add type-specific params
        if (type === 'sound-effect') {
            body.seconds_total = seconds_total;
        } else if (type === 'music') {
            body.duration = duration;
            body.instrumental = instrumental;
        }
        
        const response = await API.post(
            'main',
            `/organisation/${org_id}/integration/${integration_id}/audio_assets/generate`,
            {
                body,
                headers: { 'Content-Type': 'application/json' }
            }
        );
        return response;
    } catch (error) {
        console.error('Generate audio asset service error:', error);
        throw error;
    }
};

/**
 * Generate sound effect using CassetteAI Sound Effects Generator
 * @deprecated Use generateAudioAssetService with type='sound-effect' instead
 */
export const generateSoundEffectService = async ({ org_id, integration_id, prompt, seconds_total, socketConnectionId }) => {
    return generateAudioAssetService({
        org_id,
        integration_id,
        type: 'sound-effect',
        prompt,
        socketConnectionId,
        seconds_total
    });
};

/**
 * Fetch all audio assets for an integration with pagination
 * @param {Object} params - Fetch parameters
 * @param {string} params.org_id - Organization ID
 * @param {string} params.integration_id - Integration ID
 * @param {string} params.type - Asset type ('sound-effect' or 'music')
 * @param {number} [params.limit] - Number of items per page (default: 20)
 * @param {string} [params.next_page_token] - Token for next page
 * @param {string} [params.search] - Search query for filtering by prompt/name/category
 * @returns {Promise<Object>} List of audio assets with pagination info
 */
export const getAudioAssetsService = async ({ org_id, integration_id, type = 'sound-effect', limit = 20, next_page_token, search }) => {
    try {
        const queryStringParameters = { type, limit };
        if (next_page_token) {
            queryStringParameters.next_page_token = next_page_token;
        }
        if (search && search.trim()) {
            queryStringParameters.search = search.trim();
        }
        
        const response = await API.get(
            'main',
            `/organisation/${org_id}/integration/${integration_id}/audio_assets`,
            {
                queryStringParameters,
                headers: { 'Content-Type': 'application/json' }
            }
        );
        return response;
    } catch (error) {
        console.error('Get audio assets service error:', error);
        throw error;
    }
};

/**
 * Delete an audio asset
 * @param {Object} params - Delete parameters
 * @param {string} params.org_id - Organization ID
 * @param {string} params.integration_id - Integration ID
 * @param {string} params.asset_id - Audio asset ID
 * @param {string} params.type - Asset type ('sound-effect' or 'music')
 * @returns {Promise<Object>} Delete response
 */
export const deleteAudioAssetService = async ({ org_id, integration_id, asset_id, type = 'sound-effect' }) => {
    try {
        const response = await API.del(
            'main',
            `/organisation/${org_id}/integration/${integration_id}/audio_assets/${asset_id}`,
            {
                queryStringParameters: { type },
                headers: { 'Content-Type': 'application/json' }
            }
        );
        return response;
    } catch (error) {
        console.error('Delete audio asset service error:', error);
        throw error;
    }
};

/**
 * Fetch featured audio assets (public library) with pagination
 * @param {Object} params - Fetch parameters
 * @param {string} params.type - Asset type ('sound-effect' or 'music')
 * @param {number} [params.limit] - Number of items per page (default: 20)
 * @param {string} [params.next_page_token] - Token for next page
 * @param {string} [params.search] - Search query for filtering by prompt/name/category
 * @returns {Promise<Object>} List of featured audio assets with pagination info
 */
export const getFeaturedAudioAssetsService = async ({ type, limit = 20, next_page_token, search }) => {
    try {
        const queryStringParameters = { type, limit };
        if (next_page_token) {
            queryStringParameters.next_page_token = next_page_token;
        }
        if (search && search.trim()) {
            queryStringParameters.search = search.trim();
        }
        
        const response = await API.get(
            'main',
            `/audio_assets/featured`,
            {
                queryStringParameters,
                headers: { 'Content-Type': 'application/json' }
            }
        );
        return response;
    } catch (error) {
        console.error('Get featured audio assets service error:', error);
        throw error;
    }
};

// Backwards compatibility aliases (deprecated - use new names)
export const getSoundEffectsService = getAudioAssetsService;
export const deleteSoundEffectService = deleteAudioAssetService;
